import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  CLINIC_MARKETPLACE_PAGE,
  COACH_MARKETPLACE_PAGE,
  COURT_FINDER_PAGE,
  EXPLORE_ALL_MARKETPLACES,
  LEAGUE_MARKETPLACE_PAGE,
  OPEN_PLAY_MARKETPLACE_PAGE,
  SOCIAL_MARKETPLACE_PAGE,
  TOURNAMENTS_PAGE,
} from 'constants/pages';
import { SportsEnum } from 'types/generated/client';
import SearchIcon from 'svg/SearchIcon';
import { getExploreTypes } from 'screens/PlayerHomeV2/utils';
import Button from 'components/Button';
import { ExploreAllMarketPlacesTypesEnum } from 'components/DiscoverPage/props';
import { getSports } from 'components/Explore/Lessons/utils';
import classNames from 'styles/utils/classNames';
import AddressBar from './AddressBar';
import Dropdown from './Dropdown';

interface Props {
  isInNav?: boolean;
  defaultExploreType?: ExploreAllMarketPlacesTypesEnum;
  defaultSport?: SportsEnum;
  onAddressChange?: (coords: { latitude: number; longitude: number }) => void;
  /** Hides the search button and fires navigation immediately on any dropdown change. */
  instant?: boolean;
}

export default function SearchBar({
  isInNav,
  defaultExploreType,
  defaultSport,
  onAddressChange,
  instant,
}: Props) {
  const router = useRouter();

  const resolveSportFromUrl = (): SportsEnum | undefined => {
    const raw = router.query.sport;
    if (!raw) return undefined;
    return Object.values(SportsEnum).find((val) => val.toLowerCase() === String(raw).toLowerCase());
  };

  const [selectedSport, setSelectedSport] = useState<SportsEnum>(
    (isInNav ? resolveSportFromUrl() : undefined) ?? defaultSport ?? SportsEnum.Pickleball,
  );

  const [selectedExploreType, setSelectedExploreType] = useState(
    defaultExploreType ?? ExploreAllMarketPlacesTypesEnum.Lessons,
  );

  // Keep sport dropdown in sync when mounted in the nav.
  // Priority: URL ?sport= param (dynamic pages, user selection) > defaultSport prop
  // (static pages like /tennis/city/.../lessons where sport is in the path) > Pickleball.
  useEffect(() => {
    if (!isInNav) return;
    const fromUrl = resolveSportFromUrl();
    if (fromUrl) {
      setSelectedSport(fromUrl);
    } else {
      setSelectedSport(defaultSport ?? SportsEnum.Pickleball);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInNav, router.query.sport, defaultSport]);

  const wrapperShadowClass = 'shadow-[0px_30px_100px_0px_rgba(53,62,88,0.16)]';
  const buttonShadowClass = 'shadow-[0px_4px_20px_0px_rgba(255,92,51,0.50)]';

  const handleSportChange = (sportValue: string) => {
    setSelectedSport(sportValue as SportsEnum);
    if (isInNav && instant) {
      router.push({ query: { ...router.query, sport: sportValue.toLowerCase() } }, undefined, {
        shallow: true,
      });
    }
    if (instant) onSearch(sportValue as SportsEnum);
  };

  const handleExploreChange = (exploreValue: string) => {
    setSelectedExploreType(exploreValue as ExploreAllMarketPlacesTypesEnum);
    if (instant) onSearch(undefined, exploreValue as ExploreAllMarketPlacesTypesEnum);
  };

  const onSearch = (
    overrideSport?: SportsEnum,
    overrideExploreType?: ExploreAllMarketPlacesTypesEnum,
  ) => {
    const routes = {
      [ExploreAllMarketPlacesTypesEnum.All]: EXPLORE_ALL_MARKETPLACES,
      [ExploreAllMarketPlacesTypesEnum.Lessons]: COACH_MARKETPLACE_PAGE,
      [ExploreAllMarketPlacesTypesEnum.Courts]: COURT_FINDER_PAGE,
      [ExploreAllMarketPlacesTypesEnum.Clinics]: CLINIC_MARKETPLACE_PAGE,
      [ExploreAllMarketPlacesTypesEnum.Tournaments]: TOURNAMENTS_PAGE,
      [ExploreAllMarketPlacesTypesEnum.League]: LEAGUE_MARKETPLACE_PAGE,
      [ExploreAllMarketPlacesTypesEnum.OpenPlay]: OPEN_PLAY_MARKETPLACE_PAGE,
      [ExploreAllMarketPlacesTypesEnum.Socials]: SOCIAL_MARKETPLACE_PAGE,
    };

    const sport = overrideSport ?? selectedSport;
    const exploreType = overrideExploreType ?? selectedExploreType;

    router.push({
      pathname: routes[exploreType as keyof typeof routes],
      query: {
        sport: sport.toLowerCase(),
      },
    });
  };

  if (isInNav) {
    return (
      <>
        {/* Desktop nav search — full-width inline row */}
        <div className="hidden h-16 items-center gap-ds-sm rounded-full border border-color-border-input-lightmode px-ds-lg shadow-[0_16px_40px_0_rgba(53,62,88,0.16)] backdrop-blur-[3.7px] transition-[max-width] duration-300  ease-in-out dark:border-color-border-input-darkmode lg:flex">
          <Dropdown
            options={getSports(true)}
            selectedValue={selectedSport}
            onSelect={handleSportChange}
            wrapperClassName="min-w-[9.5rem]"
            shadowClassName="shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)]"
            buttonClassName="h-10 py-0"
            compact
          />
          <Dropdown
            options={getExploreTypes(true)}
            selectedValue={selectedExploreType}
            onSelect={handleExploreChange}
            wrapperClassName="min-w-[10rem]"
            overlayClassName="grid grid-cols-2 grid-rows-3 w-max gap-y-4"
            shadowClassName="shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)]"
            buttonClassName="h-10 py-0"
            compact
          />
          <span className="typography-product-text-card-mobile shrink-0 text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder">
            in
          </span>
          <AddressBar
            className="h-10 !w-[12.125rem] lg:py-0"
            shadowClassName="shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)]"
            onAddressChange={onAddressChange}
            isInNav
          />
          {!instant && (
            <Button
              variant="brand"
              size="icon"
              onClick={() => onSearch()}
              iconLeft={<SearchIcon className="h-4 w-4" />}
              className={classNames('h-8 w-8 shrink-0 rounded-full')}
            />
          )}
        </div>

        {/* Mobile nav search — stacked */}
        <div className="flex flex-col gap-ds-sm lg:hidden">
          <div className="flex rounded-full shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)]">
            <Dropdown
              options={getSports(true)}
              selectedValue={selectedSport}
              onSelect={handleSportChange}
              wrapperClassName="w-full max-lg:[&_button]:shadow-none max-lg:[&_button]:rounded-r-none max-lg:[&_button]:ring-0"
              shadowClassName="shadow-[0_3.03px_12.118px_0_rgba(0,0,0,0.08)]"
              buttonClassName="h-[2.625rem] py-0"
              compact
            />
            <div className="w-[1px] bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
            <Dropdown
              options={getExploreTypes(true)}
              selectedValue={selectedExploreType}
              onSelect={handleExploreChange}
              wrapperClassName="w-full max-lg:[&_button]:shadow-none max-lg:[&_button]:rounded-l-none max-lg:[&_button]:ring-0"
              overlayClassName="grid grid-cols-2 grid-rows-3 w-max max-sm:max-w-[24rem] right-0 gap-y-4"
              shadowClassName="shadow-[0_3.03px_12.118px_0_rgba(0,0,0,0.08)]"
              buttonClassName="h-[2.625rem] py-0"
              compact
            />
          </div>
          <AddressBar
            onSearch={onSearch}
            onAddressChange={onAddressChange}
            className="h-[2.625rem]"
            shadowClassName="shadow-[0_3.03px_12.118px_0_rgba(0,0,0,0.08)]"
            isInNav={isInNav}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className={classNames(
          'relative z-[2] mx-auto flex w-full max-w-[57.5rem] items-center justify-center gap-ds-lg rounded-full border border-color-border-input-lightmode bg-[rgba(255,255,255,0.85)] px-ds-2xl  py-ds-xl backdrop-blur-[5px] dark:border-color-border-input-darkmode  max-md:hidden',
          wrapperShadowClass,
        )}
      >
        <Dropdown
          options={getSports()}
          selectedValue={selectedSport}
          onSelect={handleSportChange}
          wrapperClassName="min-w-[10.25rem]"
          defaultOpen
        />

        <Dropdown
          options={getExploreTypes()}
          selectedValue={selectedExploreType}
          onSelect={handleExploreChange}
          wrapperClassName="min-w-[12.5rem]"
          defaultOpen
          overlayClassName="grid grid-cols-2 grid-rows-3 w-max gap-y-4"
        />
        <span className="typography-product-body text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder">
          in
        </span>

        <AddressBar />

        {!instant && (
          <Button
            variant="brand"
            size="lg"
            onClick={() => onSearch()}
            className={classNames('w-fit', buttonShadowClass)}
          >
            Search
          </Button>
        )}
      </div>
      <div className="relative z-[2] flex flex-col gap-ds-md md:hidden">
        <div className="flex rounded-full shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)]">
          <Dropdown
            options={getSports()}
            selectedValue={selectedSport}
            onSelect={handleSportChange}
            wrapperClassName="w-full md:w-[11.25rem] max-lg:[&_button]:shadow-none max-lg:[&_button]:rounded-r-none max-lg:[&_button]:ring-0"
            buttonClassName="h-[2.625rem] py-0"
          />
          <div className="w-px bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
          <Dropdown
            options={getExploreTypes()}
            selectedValue={selectedExploreType}
            onSelect={handleExploreChange}
            wrapperClassName="w-full md:w-[11.25rem] max-lg:[&_button]:shadow-none max-lg:[&_button]:rounded-l-none max-lg:[&_button]:ring-0"
            overlayClassName="grid grid-cols-2 grid-rows-3 w-max right-0 gap-y-4"
            buttonClassName="h-[2.625rem] py-0"
          />
        </div>

        <AddressBar isInNav={isInNav} onSearch={onSearch} className="h-[2.625rem]" />
      </div>
    </>
  );
}
